import { useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  publishRound,
  unpublishRound,
  completeRound,
  uncompleteRound,
  deleteRound,
  completeTournament,
  uncompleteTournament,
  setRoundDate,
} from '@/lib/api';
import type { Round, Tournament } from '@swiss-magic/shared-types';

type Input = {
  tournamentId: string | undefined;
  tournament: Tournament | null;
  setTournament: Dispatch<SetStateAction<Tournament | null>>;
  rounds: Round[];
  setRounds: Dispatch<SetStateAction<Round[]>>;
  selectedRound: number;
  setSelectedRound: Dispatch<SetStateAction<number>>;
  setShowFallbackNotice: Dispatch<SetStateAction<boolean>>;
  handleActionError: (err: unknown, msg: string) => void;
};

export function useRoundLifecycle({
  tournamentId,
  tournament,
  setTournament,
  rounds,
  setRounds,
  selectedRound,
  setSelectedRound,
  setShowFallbackNotice,
  handleActionError,
}: Input) {
  const { t } = useTranslation('director');
  const [showCompletionPrompt, setShowCompletionPrompt] = useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);

  async function handlePublish(round: Round) {
    if (!tournamentId) return;
    try {
      const updated = await publishRound(tournamentId, round.id);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
      setShowFallbackNotice(false);
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.publish_failed'));
    }
  }

  // Story 096 — set a round's date for the FIDE TRF report.
  async function handleSetRoundDate(roundId: string, roundDate: string | null) {
    if (!tournamentId) return;
    try {
      const updated = await setRoundDate(tournamentId, roundId, roundDate);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.round_date_failed'));
    }
  }

  async function handleUnpublish(round: Round) {
    if (!tournamentId) return;
    try {
      const updated = await unpublishRound(tournamentId, round.id);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.unpublish_failed'));
    }
  }

  async function handleCompleteRound(roundId: string) {
    if (!tournamentId) return;
    try {
      const updated = await completeRound(tournamentId, roundId);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
      // Auto-prompt: offer to complete tournament when natural endpoint reached
      if (updated.lastPlayerStanding) {
        setShowCompletionPrompt(true);
      } else if (
        tournament?.rounds != null &&
        rounds.filter((r) => r.id !== updated.id).every((r) => r.status === 'completed') &&
        rounds.length >= tournament.rounds
      ) {
        // All planned rounds completed
        setShowCompletionPrompt(true);
      }
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.complete_round_failed'));
    }
  }

  async function handleCompleteTournament() {
    if (!tournamentId) return;
    try {
      const t = await completeTournament(tournamentId);
      setTournament(t);
      setShowCompletionPrompt(false);
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.complete_tournament_failed'));
    }
  }

  async function handleUncompleteTournament() {
    if (!tournamentId) return;
    try {
      const t = await uncompleteTournament(tournamentId);
      setTournament(t);
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.uncomplete_tournament_failed'));
    }
  }

  async function handleUncomplete() {
    const round = rounds[selectedRound];
    if (!round || !tournamentId) return;
    try {
      const updated = await uncompleteRound(tournamentId, round.id);
      setRounds((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.uncomplete_round_failed'));
    }
  }

  async function handleDeleteRound() {
    const round = rounds[selectedRound];
    if (!round || !tournamentId) return;
    try {
      await deleteRound(tournamentId, round.id);
      setRounds((prev) => prev.filter((r) => r.id !== round.id));
      setSelectedRound(Math.max(0, selectedRound - 1));
    } catch (err: unknown) {
      handleActionError(err, t('rounds_page.toast.delete_round_failed'));
    }
  }

  return {
    showCompletionPrompt,
    setShowCompletionPrompt,
    showDeletePrompt,
    setShowDeletePrompt,
    handlePublish,
    handleSetRoundDate,
    handleUnpublish,
    handleCompleteRound,
    handleCompleteTournament,
    handleUncompleteTournament,
    handleUncomplete,
    handleDeleteRound,
  };
}
